import { z } from 'zod';
import { ibanSchema } from './master-data.js';
import { centAmount, isoDate, uuid } from './posting.js';
import { anschlusspruefwegSchema, clampStateSchema, fetchStatusSchema } from './zahldienste.js';
/**
 * Die Verträge der Bankanbindung (Spec 6).
 *
 * **Beträge über `centAmount`** und damit über den einen `money()`-Transformator
 * dieses Pakets: `.transform()` mit `ctx` und `z.NEVER`, nicht `superRefine`.
 * Die Begründung steht bei `parseAmount` in `posting.ts` und wird hier nicht
 * wiederholt — sie soll an einer Stelle stehen und dort altern können.
 *
 * **Die Schemas beschreiben auch AUSGABEN** (`bankAccountSchema`,
 * `bankTransactionSchema`, die beiden Ergebnisschemas). Das ist Absicht und kein
 * Zuviel: Der Lesepfad in Chunk 5b lässt seine Zeilen durch dieselben Schemas
 * laufen, mit denen das Frontend sie prüft. Eine Abfrage, die eine Spalte
 * umbenennt, fällt damit im Test auf und nicht in der Maske.
 *
 * **Die Schemas heissen `bankAccountSchema` und nicht `BankAccountSchema`.** Die
 * Spec schreibt Grossbuchstaben, dieses Paket schreibt seit `posting.ts` klein
 * (`postingLineSchema`, `documentInputSchema`, `accountInputSchema`). Der Vertrag
 * ist die **Form**, nicht die Schreibweise.
 */
export const bankProviderSchema = z.enum(['fints', 'paypal', 'mollie']);
/**
 * Woher ein Auszug beziehungsweise ein Abruffenster stammt.
 *
 * **`paypal` und `mollie` stehen ab der Zahldienste-Ära daneben** (Migration
 * `0035`). Ein Kontoauszug und ein Abruffenster sind derselbe Begriff mit zwei
 * Bezugsquellen; die Tabelle ist dieselbe, und deshalb ist es auch dieser
 * Aufzählungstyp.
 */
export const statementSourceSchema = z.enum(['upload', 'fints', 'paypal', 'mollie']);
export const matchStatusSchema = z.enum(['unmatched', 'suggested', 'matched', 'ignored']);
/**
 * Woher `external_id` stammt — die drei Werte des Bankwegs aus `0030` und die
 * zwei der Zahldienste aus `0041`.
 *
 * **Ein Ersatzschlüssel kommt bei den Zahldiensten nicht vor.** Anders als beim
 * Bankweg gibt es keinen Fall, in dem ein Dienst keinen eigenen Bezeichner
 * liefert; `paypal_txn` und `mollie_baltr` sagen einem Prüfer damit «vom
 * Anbieter vergeben».
 */
export const externalIdSourceSchema = z.enum([
    'acct_svcr_ref',
    'end_to_end_id',
    'ersatz',
    'paypal_txn',
    'mollie_baltr',
]);
/**
 * Wonach die Erkennung gesucht hat — Kundennummer, Belegnummer oder ein
 * Kennzeichen, dessen Zahl der Prüfung nicht standhält (`E-230`).
 */
export const matchKindSchema = z.enum(['aufladung', 'rechnungszahlung', 'unplausibel']);
/**
 * Der Kontierungsstand einer Bewegung (Auftrag `V-kontkomfort`) —
 * **unabhängig von `matchStatus`**: `matchStatus` gehört dem Erkennungsweg der
 * Zahldienste und der Bank-Musterliste (Belegabgleich, `D-K4`); dieses Feld
 * beantwortet die andere Frage, ob die Bewegung bereits ein Sachkonto trägt
 * (Kontierungsablage, `kontierung`/`kontierung_position`, Migration `0074`).
 *
 * - `offen` — keine Zeile in `kontierung` verweist auf diese Bewegung
 *   (dieselbe Bedingung wie `UnkontiertQuery`).
 * - `in_arbeit` — eine Kontierung ist angelegt, aber noch nicht freigegeben
 *   (Vier-Augen-Prinzip, Spec 3.6/`A-K1`).
 * - `freigegeben` — die aktuell gültige Kontierung (Kopf der Korrekturkette,
 *   `kontierung.service.ts::aktuelleFuerBewegung()`) trägt `freigegeben_am`,
 *   die Bewegung ist aber (noch) nicht gebucht.
 * - `gebucht` — `bank_transaction.journal_entry_id` ist gesetzt (Auftrag
 *   `C-kontbuchung`, Plan Task 13). **Der vierte Wert ist kein Schmuck,
 *   sondern die Grenze zwischen Stufe A und Stufe B:** Eine gebuchte Bewegung
 *   nimmt keine Korrektur mehr an (`KONTIERUNG_ALREADY_BOOKED`) — der Weg ist
 *   dann der Storno. Ohne diesen Wert müsste jede Liste raten, welche der zwei
 *   Aktionen sie anbietet, oder für jede Zeile einen eigenen Abruf starten.
 *   Er steht ABSICHTLICH nicht auf jeder freigegebenen Bewegung: `D-K6` lässt
 *   einen belegfreien Umsatz freigegeben und ungebucht (`buchung.ts`).
 */
export const kontierungStatusSchema = z.enum(['offen', 'in_arbeit', 'freigegeben', 'gebucht']);
/**
 * Die Deckungslage eines Abruffensters im Zahldienstteil des Lückenberichts
 * (`E-227`). `noch_nicht_klammerbar` ist weder gedeckt noch Lücke.
 */
export const paymentCoverageSchema = z.enum(['gedeckt', 'noch_nicht_klammerbar', 'ungedeckt']);
/** Der Behälter eines Hochladevorgangs — eine einzelne Datei oder ein Archiv. */
export const statementContainerSchema = z.enum(['zip', 'xml']);
/** Ein `sha256` in der Form, in der er gespeichert wird: 64 Kleinbuchstaben-Hexstellen. */
const sha256Form = z.string().regex(/^[0-9a-f]{64}$/, 'Kein sha256 in 64 Hexstellen');
/**
 * Eine IBAN, wie sie am Geldkonto eingegeben und gespeichert wird.
 *
 * **Revidiert (Auftrag `LS-ibaneingabe`): normalisiert statt bloss geprüft.**
 * Bis hierhin wies dieses Schema Kleinschreibung ab, mit der Begründung, ein
 * stillschweigend umgeschriebener Wert erzeuge — sobald irgendwo einmal nicht
 * umgeschrieben wird — zwei Pfade für dasselbe Konto (die IBAN wird
 * Bestandteil des Auszugsarchivpfads, Spec 8). Die Anlage einer IBAN ist die
 * EINZIGE Stelle, an der ein Bankkonto seine IBAN bekommt (kein Patch, kein
 * zweiter Endpunkt) — und {@link ibanSchema} ist seit diesem Auftrag die
 * EINZIGE Normalisierung im ganzen Vertrag, benutzt an jeder Eingabestelle
 * (Mandat, Kreditor, Aussteller-Bankkonto, Bankkonto). Ein zweiter,
 * abweichend geschriebener Pfad kann dadurch nicht mehr entstehen: Es gibt
 * nur noch die eine Schreibweise, die dieses Schema herstellt. Die
 * Prüfsumme prüft weiterhin die Datenbank über `iban_wellformed_v1` — hier
 * steht nur die Form.
 */
const ibanForm = ibanSchema;
/** Ein SKR03-Sachkonto, wie es eine Buchhalterin nennt — vier bis sechs Ziffern. */
const accountNumber = z
    .string()
    .regex(/^[0-9]{4,6}$/, 'Kontonummer besteht aus vier bis sechs Ziffern');
/**
 * Ein Geldkonto anlegen.
 *
 * **`glAccountNumber` und nicht `glAccountId`:** Ein Endpunkt, der die `uuid` des
 * Sachkontos verlangt, zwingt jeden Aufrufer zu einer Vorabfrage. Die
 * Kontonummer ist, was ein Mensch kennt — dieselbe Wahl wie in
 * `postingLineSchema`.
 *
 * **Nur EUR:** Spec 4.6 weist jeden Umsatz in Fremdwährung ab. Ein Konto in CHF
 * wäre damit ein Konto, in das nichts eingelesen werden kann.
 */
export const bankAccountCreateSchema = z
    .object({
    name: z.string().trim().min(1, 'Name fehlt').max(120),
    provider: bankProviderSchema,
    iban: ibanForm.optional(),
    bic: z
        .string()
        .regex(/^[A-Z0-9]{8}([A-Z0-9]{3})?$/, 'Keine BIC-Form')
        .optional(),
    currency: z.literal('EUR').default('EUR'),
    glAccountNumber: accountNumber,
    /**
     * Ab welchem Buchungsdatum das Journal dieses Systems für dieses Konto
     * zuständig ist (Migration `0040`, `E-239`, Spec Zahldienste `D-16`).
     *
     * **Die Pflicht trägt der VERTRAG und nicht die Datenbank.** Die Spalte ist
     * nullbar und ohne Bedingung; der Fallblock `E-211` bis `E-241` ist
     * geschlossen, und die Reichweitenaussage aus Spec 6.6 zählt auf, was die
     * Datenbank erzwingt. Ein nachträglicher `CHECK` stünde in keiner der
     * beiden Aufzählungen.
     *
     * **Und sie gilt nur für Zahldienste.** Für das bestehende Bankkonto ist
     * `O-20` offen — es gibt kein Datum, das jemand eintragen dürfte.
     */
    journalFromDate: isoDate.optional(),
})
    .strict()
    .transform((wert, ctx) => {
    // Dieselbe Bedingung wie `bank_account_iban_required` in Migration 0028.
    // Zweimal geprüft, mit Absicht: Die Datenbank setzt sie durch, der Vertrag
    // liefert die Meldung samt Pfad zum Feld.
    if (wert.provider === 'fints' && wert.iban === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['iban'],
            message: 'Ein Bankkonto ohne IBAN ist kein Bankkonto.',
        });
        return z.NEVER;
    }
    // Dieselbe Bauform mit umgekehrtem Vorzeichen: Ein Zahldienstkonto ohne
    // Zuständigkeitsbeginn holte drei Jahre Historie in ein Journal, das sie
    // nie hatte.
    if (wert.provider !== 'fints' && wert.journalFromDate === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['journalFromDate'],
            message: 'Ein Zahldienstkonto braucht den Tag, ab dem dieses Journal für es zuständig ist. ' +
                'Der Erstlauf holt Historie weit davor, und ohne diese Grenze wüsste niemand, ' +
                'welcher Teil davon in die Bücher gehört.',
        });
        return z.NEVER;
    }
    return wert;
});
/**
 * Der jüngste Auszug eines Kontos — als **ein** Objekt und nicht als drei Felder.
 *
 * **Warum verschachtelt.** Drei unabhängig nullbare Felder erlauben den Zustand
 * „Datum ja, Saldo nein", den es nicht gibt: Entweder es gibt einen Auszug, dann hat
 * er beides, oder es gibt keinen. Ein nullbares Objekt macht den Zwischenzustand
 * unausdrückbar, und das ist billiger als jede Prüfung dagegen.
 *
 * **Kein `importId` hier.** Keine Ansicht verlinkt von der Kontenliste auf eine
 * Auszugsdatei — dafür ist die Auszugsliste da (Spec 10, Ansicht 4). Ein Feld, das
 * niemand liest, ist eine Zusage ohne Leser.
 */
const lastStatementSchema = z
    .object({
    /** `from_date` — das `OPBD`-Datum, nicht das Exportdatum (4.4.1). */
    fromDate: isoDate,
    toDate: isoDate,
    /** Der Saldo laut Bank, nicht laut Journal. */
    closingBalance: centAmount,
})
    .strict();
/** Ein Geldkonto, wie es nach aussen geht. */
export const bankAccountSchema = z
    .object({
    id: uuid,
    name: z.string(),
    provider: bankProviderSchema,
    iban: ibanForm.nullable(),
    bic: z.string().nullable(),
    currency: z.string().length(3),
    glAccountId: uuid,
    glAccountNumber: z.string(),
    /**
     * Der Name des Sachkontos, etwa `Bank`.
     *
     * Er steht hier, weil Spec 10 in der Kontenliste „Sachkonto" verlangt und eine
     * Nummer allein für eine Buchhalterin keine Auskunft ist. **Kostet nichts:** Die
     * Abfrage in Chunk 5b hat den `JOIN` auf `account` ohnehin, um an die Nummer zu
     * kommen; `a.name` liegt daneben. Ihn im Browser nachzuladen wäre eine Anfrage je
     * Zeile.
     */
    glAccountName: z.string(),
    isActive: z.boolean(),
    /**
     * Der Zuständigkeitsbeginn des Journals; `null` heisst «keine Grenze
     * gesetzt» und sperrt nichts (`E-239`).
     *
     * Wer `null` als «vor allem» liest, buchte gar nichts mehr; wer es als
     * «nach allem» liest, buchte alles. Der Vergleich lautet deshalb
     * `journalFromDate !== null && tag < journalFromDate` und nirgends anders.
     */
    journalFromDate: isoDate.nullable(),
    /** `null`, solange kein Auszug eingelesen ist — der Zustand eines neuen Kontos. */
    lastStatement: lastStatementSchema.nullable(),
})
    .strict();
/**
 * Die Antworthülle von `GET /api/v1/bank-accounts` — `{ accounts: [...] }`. Der Umschlag lebt neben seinem
 * Elementschema, damit Server und Oberfläche dieselbe Hülle prüfen (Handbuch §3.969).
 */
export const bankAccountListResponseSchema = z.object({ accounts: z.array(bankAccountSchema).readonly() }).strict();
/** Ein Bankumsatz, wie er nach aussen geht. */
export const bankTransactionSchema = z
    .object({
    id: uuid,
    bankAccountId: uuid,
    importId: uuid,
    externalId: z.string(),
    externalIdSource: externalIdSourceSchema,
    bookingDate: isoDate,
    valueDate: isoDate,
    /**
     * Vorzeichenbehaftet: `DBIT` ist negativ (Spec 4.3.1, E-170). Ein Nullbetrag
     * ist ausgeschlossen — durchgesetzt von `bank_transaction_betrag_nicht_null`
     * und nicht hier, weil eine Ausgabe nicht prüft, was eine Eingabe verhindert.
     */
    amount: centAmount,
    currency: z.string().length(3),
    purpose: z.string(),
    /**
     * `<AddtlNtryInf>` -- der freie Banktext der GANZEN Buchung (z. B.
     * "DAUERAUFTRAG", "ENTGELTABSCHLUSS"), zusätzlich zu `purpose`
     * (`<RmtInf><Ustrd>`). Optional wie im Standard.
     *
     * `null` heisst zweierlei, und beides ist ehrlich: entweder die Bank hat
     * nichts mitgeliefert, oder die Zeile wurde vor Migration `0065` gelesen
     * — der Trigger aus `0031` lässt eine nachträgliche Befüllung nicht zu
     * (dieselbe Regel wie bei `matchKey` und Migration `0042`).
     */
    additionalInfo: z.string().nullable(),
    counterpartyName: z.string().nullable(),
    /**
     * Der von einem Nachbarbein ERSCHLOSSENE Name -- `E-451`, Auftrag
     * Z-namenserbe, 2026-08-27.
     *
     * **Kein Ersatz für `counterpartyName` und keine Überschreibung.** Ist
     * `counterpartyName` bereits gesetzt, ist dieses Feld immer `null` --
     * die Zusage bei `counterpartyName` bleibt unverändert wahr. Ist
     * `counterpartyName` `null`, kann dieses Feld trotzdem `null` bleiben:
     * Es wird nur gesetzt, wenn ein über `paypal_reference_id` verknüpftes
     * Nachbarbein GENAU EINEN Namen trägt (mehrdeutige Kandidaten bleiben
     * `null`, dieselbe Zurückhaltung wie bei den Guthabensperre-Paaren).
     *
     * **Kommt aus derselben Zeile, nicht aus einer zweiten Quelle.**
     * Hergeleitet zur Lesezeit aus `external_id` und `provider_reference`
     * (`apps/api/src/bank/bank.query.ts::ERSCHLOSSENER_NAME_LATERAL`) --
     * beide Spalten trägt jede PayPal-Zeile bereits, ob über den Abruf-
     * oder den Dateiweg eingelesen. Gemessen (Untersuchung Z-payername,
     * 2026-08-26): 94 von 358 archivierten `T0300`-Beinen (Kontoaufladung)
     * tragen keinen eigenen Namen, aber ihr auslösendes Zahlungsbein tut es.
     *
     * Die Oberfläche zeigt diesen Wert erkennbar ABGELEITET (kursiv, mit
     * Titel «vom auslösenden Vorgang übernommen») und nicht wie eine eigene
     * Angabe des Anbieters.
     */
    counterpartyNameInherited: z.string().nullable(),
    counterpartyIban: z.string().nullable(),
    isBatch: z.boolean(),
    batchCount: z.number().int().positive().nullable(),
    feeAmount: centAmount.nullable(),
    matchStatus: matchStatusSchema,
    /**
     * Der von der Erkennung gefundene Schlüssel — die reine Ziffernfolge, das
     * Kennzeichen `FS-` beziehungsweise `FA-` abgeschnitten (`E-234`).
     * `null` heisst **kein Treffer**.
     *
     * **Er steht hier, weil die Klärliste ohne ihn ihre eine Aussage nicht
     * machen kann.** `E-231` verlangt, dass «kein Treffer» und «Treffer, Ziel
     * nicht gefunden» nicht zusammenfallen: Beide sind `unmatched`, und **nur
     * der zweite trägt `matchKey`** — daran allein sind sie zu unterscheiden.
     * Der erste deutet auf eine unbekannte Schreibweise, der zweite auf
     * fehlende Stammdaten. Wer beide gleich behandelt, sucht das eine Problem
     * und findet das andere nicht.
     *
     * Migration `0042` schreibt die Spalte beim Einfügen und nie danach; der
     * Trigger aus `0031` lässt sie nicht mehr ändern. Ein Bestand, der vor
     * `0042` geholt wurde, trägt sie deshalb nie — dort steht `null`, und das
     * ist ehrlich.
     */
    matchKey: z.string().nullable(),
    /**
     * Wonach gesucht wurde: `aufladung` (Kundennummer), `rechnungszahlung`
     * (Belegnummer) oder `unplausibel` (Kennzeichen getroffen, Zahl hält der
     * Prüfung nicht stand, `E-230`).
     *
     * **`null` heisst «es ist nichts gesucht worden»** und ist von «gesucht und
     * nichts gefunden» verschieden — der zweite Fall trägt `unplausibel` mit
     * dem gefundenen Text. Eine Auszahlung ist der Regelfall für `null`: Sie
     * gehört zu keiner Rechnung (`E-232`).
     */
    matchKind: matchKindSchema.nullable(),
    /**
     * Der Kontierungsstand — siehe `kontierungStatusSchema`. Seit
     * `V-kontkomfort`, damit die Umsatzliste (Bank- UND Zahldienstkonten,
     * `GET :id/transactions` bedient beide) eine direkte «Kontieren»-Aktion je
     * Zeile anbieten kann, ohne für jede Zeile einen eigenen Abruf zu starten.
     */
    kontierungStatus: kontierungStatusSchema,
})
    .strict();
/**
 * Der Suchbegriff der Umsatzliste.
 *
 * **`.trim()` und dann «leer heisst kein Filter».** Ein Feld, aus dem der
 * Anwender den Text gelöscht hat, hinterlässt oft ein Leerzeichen; ein
 * Suchbegriff aus einem Leerzeichen fände in der Suchform (Leerraum wird
 * gestrafft und die Ränder fallen weg) **jede** Zeile und sähe damit aus wie
 * kein Filter — nur eben zufällig. Der Vertrag macht daraus ausdrücklich
 * `undefined`, damit die Aussage «leer ist kein Filter» eine Zusage ist und
 * keine Nebenwirkung.
 *
 * **`max(200)` und keine Mindestlänge.** Eine Untergrenze wäre die naheliegende
 * Bequemlichkeit und wäre falsch: Am Echtbestand liefert `7605` zwei Treffer,
 * und wer eine dreistellige Belegnummer sucht, sucht sie. Die Obergrenze ist
 * dagegen echt — kein Verwendungszweck dieses Bestandes ist länger als 135
 * Zeichen (gemessen am 2026-08-21), und ein Begriff von 10.000 Zeichen ist kein
 * Suchbegriff, sondern eine Last, die je Zeile ausgewertet würde.
 */
export const bankSearchTermSchema = z
    .string()
    .max(200, 'Der Suchbegriff ist länger als jeder Verwendungszweck sein kann')
    .transform((wert) => wert.trim())
    .transform((wert) => (wert === '' ? undefined : wert));
/**
 * Die Umsatzliste, wie sie nach aussen geht.
 *
 * **`total` steht daneben, weil die Liste ohne ihn ihre eine Aussage nicht
 * machen kann:** «nichts gefunden» und «nichts vorhanden» wären sonst
 * dieselbe leere Tabelle. Es ist die Zahl **aller** Treffer der Filter und
 * nicht die der gelieferten Seite — die steht in `transactions.length`.
 */
export const bankTransactionListSchema = z
    .object({
    transactions: z.array(bankTransactionSchema),
    total: z.number().int().nonnegative(),
    /**
     * Die Zahl **aller** Bewegungen des Kontos, ohne jeden Filter.
     *
     * **Sie steht daneben, weil `total` sich sonst als Gesamtzahl ausgibt**
     * (Befund `W-seitenwahl`, gemeldet vom Auftraggeber am 2026-08-21). Die
     * Bewegungsliste eines Zahldienstkontos ist mit `matchStatus=unmatched`
     * vorbelegt — fachlich richtig (Spec 13, Punkt 2) und die Ursache dafür,
     * dass über der Tabelle **5.229** stand, während das Konto **5.434**
     * Bewegungen trägt. Der Auftraggeber hat seine PayPal-Oberfläche daneben
     * gehalten und daraus zwangsläufig geschlossen, ein Umsatz fehle: Die
     * vermisste Auszahlung über 2.100,00 € vom 19.08.2026 ist importiert und
     * steht auf `ignored`, weil eine Auszahlung auf das eigene Bankkonto zu
     * keiner Rechnung gehört (`E-232`).
     *
     * **Zwei Zahlen und kein Wahrheitswert «gefiltert».** Ein Kennzeichen
     * sagte, **dass** gefiltert wird, und nicht **wie viel** dabei wegfällt —
     * und genau die Differenz ist die Auskunft, die den Fehlschluss verhindert.
     *
     * Sie ist Pflicht und nicht `.optional()`: dieselbe Begründung wie bei
     * `total`. Ein Feld, das fehlen darf, zwingt jede Anzeige zu einem
     * Ersatzwert, und jeder Ersatzwert an dieser Stelle wäre eine Behauptung
     * über den Bestand.
     */
    accountTotal: z.number().int().nonnegative(),
    /**
     * Wie viele Zeilen der technische Filter aus DIESER Antwort entfernt hat
     * (`Z-guthabensperre`, 2026-08-23).
     *
     * Guthabensperre und ihre Aufhebung sind per Voreinstellung ausgeblendet
     * — Weisung des Auftraggebers, der unsere Liste neben BuchhaltungsButler
     * gehalten und diese Zeilen dort vermisst hat, weil sie ihn verwirren.
     * `0`, solange der Umschalter "Technische Zeilen anzeigen" steht ODER
     * kein geschlossenes Paar im aktuellen Filterfenster liegt — beides sind
     * echte Nullen und keine Behauptung.
     *
     * **Pflicht und nicht `.optional()`, aus demselben Grund wie
     * `accountTotal`.** Ein Feld, das fehlen darf, zwingt jede Anzeige zu
     * einem Ersatzwert, und jeder Ersatzwert wäre hier eine Behauptung über
     * eine Ausblendung, die vielleicht gar nicht stattfindet.
     */
    technicalHidden: z.number().int().nonnegative(),
})
    .strict();
/**
 * Ein eingelesener Auszug in der Auszugsliste (Spec 10, Ansicht 4).
 *
 * **Der Schlüssel heisst `importId` und nicht `id`.** Es ist derselbe Wert — die
 * Kennung des `bank_statement_import` —, aber der Name ist an drei anderen Stellen
 * dieses Vertrags schon vergeben: `statementImportResultSchema.importId`,
 * `alreadyImported[].importId` und `bankTransactionSchema.importId`. Dazu heisst der
 * Pfad `…/statements/:importId/file`, und genau diesen Wert muss die Oberfläche
 * dorthin schicken. Ein `id` in der vierten Stelle wäre die Verwechslung, die auf
 * `string` typprüft und erst am 404 auffällt.
 */
export const bankStatementSchema = z
    .object({
    importId: uuid,
    bankAccountId: uuid,
    source: statementSourceSchema,
    messageType: z.string(),
    /**
     * Die Prüfsumme der Originaldatei — **`null` bei einem Abruffenster**.
     *
     * Migration `0037` löst die Pflicht für `sha256`, `storage_key` und
     * `byte_size` und schreibt beide Richtungen als `CHECK` fest: Ein
     * Kontoauszug **muss** eine Datei tragen, ein Abruffenster **darf keine**
     * tragen. Der Beleg eines Fensters ist die archivierte Antwort in
     * `payment_fetch_response` (`E-226`).
     */
    sha256: sha256Form.nullable(),
    fromDate: isoDate,
    toDate: isoDate,
    /**
     * Der Zustand des Laufs (`bank_statement_import.fetch_status`, Migration
     * `0038`).
     *
     * **Er steht hier, weil ohne ihn jedes `null` darunter unerklärt bliebe.**
     * Ein Abruffenster mit `gescheitert` trägt keine Salden und keine
     * Bewegungszahlen; eine Liste, die dafür einen Strich zeigt und nicht sagt,
     * warum, verlangt vom Leser dieselbe Vermutung, die `E-218` verbieten soll.
     * Der CAMT-Weg setzt die Spalte nie und trägt deshalb den Vorgabewert
     * `uebernommen` — für eine hochgeladene Datei ist das die Wahrheit.
     */
    fetchStatus: fetchStatusSchema,
    /**
     * Die vom Anbieter beziehungsweise der Datei gerechneten Ränder —
     * **nullbar, und das ist der Kern von `Z-zeilenistkeinauszug`**.
     *
     * **Bis zum Abrufweg entstand eine Zeile in `bank_statement_import` nur,
     * wenn eine Datei gelesen worden war.** «Es gibt eine Zeile» und «es gibt
     * einen Auszug mit Salden» waren dasselbe. Seit dem Abruf entsteht eine
     * Zeile **auch dann, wenn nichts übernommen wurde** — mit Zeitraum, aber
     * ohne Salden, ohne `sha256`, ohne Datei. Migration `0038` hat die
     * `NOT NULL` dafür gelöst und die Pflicht über
     * `bank_statement_import_zahlen_bei_uebernahme` genau auf die übernommenen
     * Zeilen zurückgeholt; dieser Vertrag hat es drei Fassungen lang nicht
     * nachvollzogen. Der Preis war eine `500` auf der ganzen Auszugsliste:
     * `fromDb(null)` wirft `MoneyParseError`, und das PayPal-Konto trägt
     * **elf** solcher Zeilen (gemessen am 2026-08-21: zehn `gescheitert`, eine
     * `verworfen`).
     *
     * **Nullbar und kein eigener Zustand.** Ein zweites Feld — etwa
     * `balances: {opening, closing} | null` — behauptete, die zwei Ränder
     * könnten getrennt fehlen; sie können es nicht: Der `CHECK` verlangt beide
     * oder keinen. Und ein Ersatzwert `0.00` wäre die schlimmste der drei
     * Formen: Ein Anfangssaldo von null ist eine **Aussage**, die ein
     * gescheitertes Fenster gerade nicht macht.
     */
    openingBalance: centAmount.nullable(),
    closingBalance: centAmount.nullable(),
    /**
     * Die Bewegungszahlen — `null`, solange nichts übernommen ist.
     *
     * `bank_statement_import_keine_zahl_ohne_uebernahme` verbietet die `0` an
     * einer nicht übernommenen Zeile ausdrücklich: Sie sähe aus wie ein leeres
     * Fenster, und das ist genau die Verwechslung, gegen die `E-218` gebaut
     * ist.
     */
    bookedCount: z.number().int().nonnegative().nullable(),
    skippedCount: z.number().int().nonnegative().nullable(),
    /**
     * Umsätze der Datei, die es am Konto bereits gab — der überlappende
     * Auszug, der Normalfall (Spec 7.3, Migration `0032`).
     *
     * **NICHT nullbar, anders als `bookedCount`/`skippedCount`.** Die Spalte
     * `bank_statement_import.duplicate_count` ist `NOT NULL DEFAULT 0` und
     * blieb das auch in Migration `0038` — sie trägt an einem nicht
     * übernommenen Fenster denselben Vorgabewert wie an einem übernommenen
     * ohne Dubletten. Die Oberfläche unterscheidet die beiden Fälle über
     * `fetchStatus`, nicht über diese Zahl (Handbuch, `dublettenzahl()` in
     * `apps/web/src/bank/auszug.ts`).
     */
    duplicateCount: z.number().int().nonnegative(),
    /** Die Grösse der Originaldatei; `null` beim Abruffenster (Migration `0037`). */
    byteSize: z.number().int().nonnegative().nullable(),
    importedAt: z.string(),
})
    .strict();
/**
 * Die Antworthülle von `GET /api/v1/bank-accounts/:id/statements` — `{ statements: [...] }`. Der Umschlag lebt neben seinem
 * Elementschema, damit Server und Oberfläche dieselbe Hülle prüfen (Handbuch §3.969).
 */
export const bankStatementListResponseSchema = z.object({ statements: z.array(bankStatementSchema).readonly() }).strict();
/**
 * Das Ergebnis für EINE Datei (Spec 6).
 *
 * **`entryName` ist das elfte Feld** und steht nicht in der Aufzählung der Spec.
 * Der Satz direkt unter dem Block verlangt es: „`entryName` steht in jedem
 * Zweig". Bei 250 Dateien ist ein Ergebnis ohne Namen nicht zuzuordnen — und für
 * eine einzeln hochgeladene Datei ist es der Name aus dem Upload.
 *
 * **`.strict()` hat hier eine Folge, die die Naht bindet:** Der Dienst liefert in
 * `StatementImportResult` zusätzlich `storageKey` (am Code abgelesen,
 * `apps/api/src/bank/statement-import.service.ts`). Ein Endpunkt kann den Bericht
 * deshalb **nicht** unverändert durchreichen, sondern muss den Pfad abziehen.
 * Diese Zusage ist geprüft — die Prüfung, die ein Ergebnis mit `storageKey`
 * ablehnt, steht in `test/bank.test.ts`, und sie fängt genau den Fall, den ein
 * `satisfies` nicht sieht: Ein `.strict()`-Verstoss ist kein Typfehler.
 */
export const statementImportResultSchema = z
    .object({
    entryName: z.string(),
    importId: uuid,
    messageType: z.string(),
    /**
     * Der `sha256` der eingelesenen Datei.
     *
     * **Steht im Vertrag und nicht bloss in der Datenbank.** Er benennt das
     * Archivstück, gegen das `pnpm bank:archive-verify` vergleicht; ohne ihn müsste
     * die Oberfläche die Auszugsliste befragen, um einen Wert zu erfahren, den die
     * Antwort schon hatte. `storageKey` bleibt dagegen **innen** — er ist der Pfad
     * im Archiv des Betreibers, und nach aussen führt
     * `…/statements/:importId/file` (Spec 6, Spec 8).
     */
    sha256: sha256Form,
    fromDate: isoDate,
    toDate: isoDate,
    bookedCount: z.number().int().nonnegative(),
    /** Vorgemerkte und INFO-Umsätze. Gehen NICHT in die Saldo-Invariante ein. */
    skippedCount: z.number().int().nonnegative(),
    /** Umsätze, die es schon gab — überlappender Auszug, der Normalfall (E-181). */
    duplicateCount: z.number().int().nonnegative(),
    openingBalance: centAmount,
    closingBalance: centAmount,
    // KEIN `alreadyImported`. Dieses Schema beschreibt eine Datei, die eingelesen
    // WURDE; eine uebersprungene hat weder eigene `importId` noch eigenes
    // Saldenpaar. Sie steht als Liste an der Huelle (Spec 7.3, Spec Revision 4).
})
    .strict();
/** Ein Anschlussbruch, wie ihn die Prüfung über die sortierte Folge meldet (4.4.2). */
export const continuityBreakSchema = z
    .object({
    before: z
        .object({ entryName: z.string(), toDate: isoDate, closingBalance: centAmount })
        .strict(),
    after: z
        .object({ entryName: z.string(), fromDate: isoDate, openingBalance: centAmount })
        .strict(),
})
    .strict();
/**
 * Das Hüllschema: ein Hochladevorgang liefert n Dateien (Spec 6, Spec 4.0).
 *
 * `statementImportResultSchema` allein ist einzahlig und trägt weder die
 * übergangenen Nicht-XML-Einträge noch den Anschlussbefund noch die Summe.
 */
export const statementImportBatchResultSchema = z
    .object({
    container: statementContainerSchema,
    /** XML-Dateien im Archiv. Bei `container: 'xml'` immer 1. */
    fileCount: z.number().int().nonnegative(),
    /** Nicht-XML-Einträge, mit Namen (E-199). */
    skippedEntries: z.array(z.string()),
    imported: z.array(statementImportResultSchema),
    /**
     * Je übersprungene Datei ein Eintrag — eine **Liste** und kein Wahrheitswert
     * (Spec 7.3, entschieden am 2026-08-19).
     *
     * Ein Wahrheitswert kann die Frage nicht beantworten, die bei einem Archiv
     * gestellt wird: Wer im Januar ein Halbjahr geladen hat und im Juli das Jahr
     * lädt, hat 125 übersprungene und 125 neue Einträge in **einem** Bericht.
     * `true` sagte dann „irgendetwas war schon da". Umgekehrt trägt die Liste den
     * Wahrheitswert: `alreadyImported.length === 1 && imported.length === 0` ist
     * er.
     */
    alreadyImported: z.array(z
        .object({
        entryName: z.string(),
        sha256: sha256Form,
        /** Der FRÜHERE Import. Der Anwender kann ihn nachsehen. */
        importId: uuid,
    })
        .strict()),
    /**
     * Befund der Anschlussprüfung. `ok: false` ist **kein Fehler** — der Import
     * lief durch (Spec 4.4.2, Entscheidung 3 in 7.1).
     */
    continuity: z.object({ ok: z.boolean(), breaks: z.array(continuityBreakSchema) }).strict(),
    totals: z
        .object({
        bookedCount: z.number().int().nonnegative(),
        skippedCount: z.number().int().nonnegative(),
        duplicateCount: z.number().int().nonnegative(),
    })
        .strict(),
})
    .strict();
/** Eine Gruppe der Klärliste eines Abruffensters (`E-228`). */
export const nichttreffergruppeSchema = z
    .object({
    /** Die verkürzte Form: jede Ziffernfolge durch ein Zeichen. */
    form: z.string(),
    count: z.number().int().positive(),
    /** Unverkürzt, damit die Gruppe nachschlagbar ist. */
    example: z.string(),
})
    .strict();
/** Ein Abruffenster im Deckungsbericht eines Zahldienstkontos (`E-227`). */
export const paymentWindowSchema = z
    .object({
    importId: uuid,
    from: isoDate,
    to: isoDate,
    /** Der Abrufzustand aus Migration `0038` (`E-218`). */
    fetchStatus: z.enum(['uebernommen', 'verworfen', 'gescheitert']),
    /**
     * Die **drei** Zustände (`E-227`, `E-215`). `noch_nicht_klammerbar`
     * ist weder gedeckt noch Lücke: Ein Fenster, dessen Ende jünger als
     * ein Tag ist, darf abgerufen sein und gilt trotzdem nicht als
     * nachgewiesen.
     */
    coverage: paymentCoverageSchema,
    /**
     * Der Klammerzustand HINTER `coverage` — dieselbe Vokabel wie
     * `fetchReportSchema.clamp` (`E-422`). `coverage` und `clamp`
     * laufen zusammen (`gedeckt` ⟺ `stimmig`, `ungedeckt` ⟺
     * `gebrochen`), stehen aber beide im Vertrag, damit ein Leser,
     * der beide Berichte kennt, nicht zwei Wörter für dieselbe
     * Sache lernen muss.
     */
    clamp: clampStateSchema,
    /**
     * Stammt das Fenster aus der Frischschiene (`Z-frischtakt`)?
     * `true` heisst: nie bewiesen, `clamp` bleibt deshalb
     * dauerhaft `noch_nicht_klammerbar` (`E-423`) und das Fenster
     * bleibt aus der Anschlussprüfung dieses Berichts aussen vor
     * (`E-424`).
     */
    frisch: z.boolean(),
    /** Bewegungen, die eine Zuordnung bekommen sollten — ohne `ignored`. */
    considered: z.number().int().nonnegative(),
    unmatched: z.number().int().nonnegative(),
    /** Die Nichttrefferquote je Abruf (`E-228`). Ein Anteil, kein Betrag. */
    missRate: z.number().min(0).max(1),
    /** Die Klärliste, nach Textform gruppiert und ausgezählt (`E-228`). */
    groups: z.array(nichttreffergruppeSchema),
})
    .strict();
/**
 * Ein Zeitraum ohne Abruf im Zahldienstteil des Deckungsberichts (`E-227`, `E-363`, `E-384`).
 * Die Herleitung der drei Fälle steht an `paymentServiceCoverageSchema.gaps`.
 */
export const zahldienstLueckeSchema = z
    .object({
    pruefweg: anschlusspruefwegSchema,
    luecke: z.boolean(),
    after: z.object({ importId: uuid, toDate: isoDate, closingBalance: centAmount }).strict(),
    before: z.object({ importId: uuid, fromDate: isoDate, openingBalance: centAmount }).strict(),
})
    .strict();
/**
 * Der Zahldienstteil des Deckungsberichts (Spec Zahldienste 9.1) — nur bei einem Konto mit
 * `provider` `paypal`/`mollie`.
 */
export const paymentServiceCoverageSchema = z
    .object({
    /** Je Abruffenster eine Zeile, nach `to_date` geordnet. */
    windows: z.array(paymentWindowSchema),
    /**
     * Zeiträume ohne Abruf (`E-227`) — **und nicht dieselbe Rechnung wie
     * `gaps` eine Ebene höher.**
     *
     * Ein hochgeladener Auszug trägt `OPBD`/`CLBD` desselben Kalendertages
     * und überlappt sich mit seinem Nachbarn um einen Tag; dort ist
     * angrenzend `to_date(A) = from_date(B)`. Ein Abruffenster ist ein
     * **einschliessender** Tagesbereich, und angrenzend heisst **Folgetag**
     * (`E-214`). Eine Liste für beide meldete jedes saubere Fensterpaar als
     * Lücke und ein wirklich angrenzendes Paar als heil.
     *
     * **Drei Fälle und nicht einer** (`E-363`, `E-384`). Vor dieser Fassung
     * trug jede Zeile nur die zwei Randangaben, und ein geprüfter, aber
     * brechender Überlappungsbefund war von einer echten Lücke nicht zu
     * unterscheiden — die Handlungsanweisung ist aber verschieden: Eine
     * Lücke wird nachgeholt, ein Überlappungsbruch verlangt, die Salden zu
     * klären. `pruefweg` sagt, gegen welches Fenster geprüft wurde
     * (derselbe Vertrag wie `Anschlussbruch.pruefweg` in
     * `continuity.ts`); `luecke` sagt bei `pruefweg: 'angrenzend'`, ob
     * wirklich eine Zeitspanne fehlt (`naechsterTag(after) <>
     * before.von`) oder nur die Randsalden auseinanderliegen. Bei
     * `pruefweg: 'ueberlappend'` ist `luecke` immer `false`: Eine
     * Überlappung ist per Definition keine fehlende Zeitspanne, sondern ein
     * Saldowiderspruch am Übergang. Eine nicht prüfbare Überlappung
     * (`ueberlappungenOhnePruefung` in `continuity.ts`) erscheint hier
     * **nicht** — sie ist kein Befund.
     */
    gaps: z.array(zahldienstLueckeSchema),
    /**
     * Die **sichtbare** Nichtbuchung (`E-240`).
     *
     * In Stufe A sind das alle Bewegungen — und genau deshalb muss der
     * Bericht es sagen. Eine Nichtbuchung, die niemand sieht, ist von einer
     * vergessenen Buchung nicht zu unterscheiden.
     */
    notBooked: z
        .object({
        count: z.number().int().nonnegative(),
        /**
         * Die offenen Punkte, an denen die Buchung hängt. Seit dem
         * 2026-10-15 leer: Die Fragekennungen der Entwicklung (`O-7` bis
         * `O-13`) gingen bis dahin wörtlich an den Kunden (`E-1976`).
         */
        openQuestions: z.array(z.string()),
        reason: z.string(),
    })
        .strict(),
    /**
     * Das Konto-Aggregat der Klammerqualität (`E-425`).
     *
     * `bewiesenBis` ist der letzte Tag der lückenlosen, ab dem ersten
     * Fenster ununterbrochenen Kette klammer-bewiesener Fenster —
     * `null`, solange nicht einmal das erste Fenster bewiesen ist. Die
     * Frischschiene zählt hier nie mit, sie überlappt mit Absicht.
     * `unbewiesenBis` ist der weiteste Tag, den ein `uebernommen`-Fenster
     * JENSEITS von `bewiesenBis` schon zeigt — typischerweise ein
     * Fenster der Frischschiene oder ein frischer Handabruf; `null`
     * heisst, es gibt nichts Ungewisses über den bewiesenen Rand hinaus.
     */
    beweislage: z
        .object({
        bewiesenBis: isoDate.nullable(),
        unbewiesenBis: isoDate.nullable(),
    })
        .strict(),
})
    .strict();
/** Eine Lücke im Bestand eines Bankkontos (Spec 9.1). */
export const coverageGapSchema = z
    .object({
    /** `to_date` des früheren Auszugs. */
    afterToDate: isoDate,
    /** `from_date` des späteren. Eine Lücke ist `afterToDate <> beforeFromDate`. */
    beforeFromDate: isoDate,
    /**
     * Zwischen den beiden Auszügen fehlt eine Zeitspanne — die Spalte
     * `luecke` der Sicht `bank_statement_anschluss` (Migration 0033).
     *
     * Die Sicht liefert eine Zeile, sobald **eine** der beiden Flaggen
     * zutrifft; ohne dieses Feld sagt die Zeile allein nicht, welche. Ein
     * Leser, der jede Zeile «Lücke» nennt, behauptet für einen reinen
     * Saldobruch einen fehlenden Tag, den es nicht gibt. Handbuch §3.79.
     */
    gap: z.boolean(),
    /** Zusätzlich bricht der Saldo — dann ist es ein Widerspruch (9.1). */
    balanceBreak: z.boolean(),
})
    .strict();
/**
 * Der Lückenbericht (Spec 6, Definition in 9.1).
 *
 * `from` und `to` sind der Rand des vorhandenen Bestandes und **nullbar**: Ein
 * Konto ohne Auszug hat keinen Rand, und ein erfundener wäre eine Behauptung.
 */
export const coverageReportSchema = z
    .object({
    bankAccountId: uuid,
    from: isoDate.nullable(),
    to: isoDate.nullable(),
    statementCount: z.number().int().nonnegative(),
    gaps: z.array(coverageGapSchema),
    /**
     * Der Zahldienstteil — **nur** bei einem Konto mit `provider`
     * `paypal`/`mollie` (Spec Zahldienste 9.1).
     *
     * **Optional und nicht mit Nullen gefüllt.** Ein Bankkonto bekäme sonst
     * einen Abschnitt mit lauter Nullen, und ein Leser müsste raten, ob das
     * „keine Abrufe" oder „kein Zahldienst" heisst. Der Bankbericht bleibt
     * damit Zeichen für Zeichen der, der er war — geprüft in
     * `apps/api/test/bank/coverage.test.ts`, das gegen dieses `.strict()`
     * Schema parst.
     *
     * **Das Feld ist am Vertrag nötig und nicht Kür.** Das Schema ist
     * `.strict()`; ein Dienst, der die drei Zustände und die Nichtbuchung
     * zurückgäbe, ohne dass sie hier stehen, liefe beim ersten `parse` in
     * einen Vertragsbruch.
     */
    paymentService: paymentServiceCoverageSchema.optional(),
})
    .strict();
/**
 * Die Anfrage: Konto und Datei (Spec 6).
 *
 * Mehr steht nicht darin, und das ist die Aussage. Die **Art** des Behälters
 * kommt aus den magischen Bytes und nicht aus der Endung (7.1, Stufe A), der
 * **Zeitraum** aus den Saldodaten und nicht aus dem Namen (4.4.1), die
 * **Reihenfolge** aus dem OPBD-Datum und nicht aus dem Dateinamen (E-200). Der
 * Name ist deshalb reine Auskunft für den Bericht — geprüft wird er trotzdem:
 * Ein Wert, der wie ein Pfad aussieht, wird irgendwann als einer benutzt.
 */
export const statementImportRequestSchema = z
    .object({
    bankAccountId: uuid,
    filename: z
        .string()
        .min(1, 'Dateiname fehlt')
        .max(255)
        .regex(/^[^/\\]+$/, 'Der Dateiname darf keinen Pfadanteil haben')
        .refine((n) => n !== '.' && n !== '..', 'Kein gültiger Dateiname'),
})
    .strict();
//# sourceMappingURL=bank.js.map